import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { colors } from '../theme/colors.stylex';
import { controlMetrics } from '../theme/controls.stylex';
import type { SegmentedControlProps } from './SegmentedControl.types';

const styles = stylex.create({
  root: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  pill: {
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    cursor: 'pointer',
    borderRadius: '9999px',
    borderStyle: 'solid',
    borderWidth: controlMetrics.borderWidth,
    paddingBlock: '0.375rem',
    paddingInline: '0.75rem',
    transitionProperty: 'all',
    transitionDuration: '150ms',
    transitionTimingFunction: 'ease-out',
    outlineStyle: { 'default': null, ':focus-visible': 'none' },
    boxShadow: {
      'default': 'none',
      ':focus-visible': `0 0 0 1px ${colors.background}, 0 0 0 3px color-mix(in srgb, ${colors.link} 30%, transparent)`,
    },
    backgroundImage: 'none',
    backgroundColor: { 'default': colors.segmentBackground, ':hover': colors.segmentHover },
    borderColor: colors.segmentBorderInactive,
    color: { 'default': colors.bodyText, ':hover': colors.menuText },
  },
  active: {
    backgroundColor: { 'default': colors.segmentSelected, ':hover': colors.segmentSelectedHover },
    borderColor: colors.segmentBorder,
    color: { 'default': colors.menuText, ':hover': colors.menuText },
  },
});

function SegmentedControl<T extends string | number | boolean>({
  options,
  value,
  onChange,
  extraClassNames = '',
  xstyle,
  id,
  testId,
  ariaLabel,
}: SegmentedControlProps<T>) {
  const rootProps = stylex.props(styles.root, xstyle);

  return (
    <div
      aria-label={ariaLabel}
      {...rootProps}
      className={clsx(rootProps.className, extraClassNames)}
      data-testid={testId}
      id={id}
      role="group">
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            aria-pressed={isActive}
            {...stylex.props(styles.pill, isActive && styles.active)}
            key={String(option.value)}
            onClick={onChange ? () => onChange(option.value) : undefined}
            type="button">
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
